/**
 * Permission catalog + role matrix, frontend mirror of the shore primitives.
 * Keep in sync with `pulsewise-shore/src/shared/auth/permissions.ts` and
 * `role-matrix.ts`. (Promoting these to a shared package is deferred to
 * tooling work, for now, both copies are intentional duplicates.)
 *
 * Source of truth on the backend; the frontend uses these to gate UI affordances
 * (hide/disable buttons). The backend is the security boundary.
 */

export const Permission = {
  ACCOUNT_CREATE_WORKSPACE: "ACCOUNT_CREATE_WORKSPACE",
  ACCOUNT_DELETE_WORKSPACE: "ACCOUNT_DELETE_WORKSPACE",
  ACCOUNT_RESTORE_WORKSPACE: "ACCOUNT_RESTORE_WORKSPACE",
  ACCOUNT_GRANT_ADMIN: "ACCOUNT_GRANT_ADMIN",
  ACCOUNT_REVOKE_ADMIN: "ACCOUNT_REVOKE_ADMIN",
  ACCOUNT_LIST_WORKSPACES: "ACCOUNT_LIST_WORKSPACES",
  ACCOUNT_EDIT_NAME: "ACCOUNT_EDIT_NAME",
  ACCOUNT_MANAGE_AI_CONNECTIONS: "ACCOUNT_MANAGE_AI_CONNECTIONS",

  WORKSPACE_VIEW: "WORKSPACE_VIEW",
  WORKSPACE_EDIT_SETTINGS: "WORKSPACE_EDIT_SETTINGS",
  WORKSPACE_INVITE_MEMBER: "WORKSPACE_INVITE_MEMBER",
  WORKSPACE_REVOKE_INVITE: "WORKSPACE_REVOKE_INVITE",
  WORKSPACE_LIST_INVITATIONS: "WORKSPACE_LIST_INVITATIONS",
  WORKSPACE_REMOVE_MEMBER: "WORKSPACE_REMOVE_MEMBER",
  WORKSPACE_CHANGE_MEMBER_ROLE: "WORKSPACE_CHANGE_MEMBER_ROLE",
  WORKSPACE_LIST_MEMBERS: "WORKSPACE_LIST_MEMBERS",
  MEMBERSHIP_EDIT_MEMBER_IDENTITY: "MEMBERSHIP_EDIT_MEMBER_IDENTITY",
  WORKSPACE_LEAVE: "WORKSPACE_LEAVE",

  PROFILE_EDIT_SELF: "PROFILE_EDIT_SELF",
  PROFILE_VIEW_SELF: "PROFILE_VIEW_SELF",

  // Module 2: People & Org Structure (mirror of shore)
  DEPARTMENT_CREATE: "DEPARTMENT_CREATE",
  DEPARTMENT_EDIT: "DEPARTMENT_EDIT",
  DEPARTMENT_DELETE: "DEPARTMENT_DELETE",
  DEPARTMENT_RESTORE: "DEPARTMENT_RESTORE",
  DEPARTMENT_LIST: "DEPARTMENT_LIST",
  MEMBERSHIP_EDIT_HR_FIELDS: "MEMBERSHIP_EDIT_HR_FIELDS",
  MEMBERSHIP_EDIT_SELF_PREFS: "MEMBERSHIP_EDIT_SELF_PREFS",
  MEMBERSHIP_EDIT_MEMBER_PREFS: "MEMBERSHIP_EDIT_MEMBER_PREFS",
  MEMBERSHIP_ASSIGN_MANAGER: "MEMBERSHIP_ASSIGN_MANAGER",
  WORKSPACE_IMPORT_USERS: "WORKSPACE_IMPORT_USERS",
  WORKSPACE_BULK_EDIT_MEMBERS: "WORKSPACE_BULK_EDIT_MEMBERS",
  WORKSPACE_BULK_ASSIGN_MANAGER: "WORKSPACE_BULK_ASSIGN_MANAGER",
  WORKSPACE_BULK_REMOVE_MEMBERS: "WORKSPACE_BULK_REMOVE_MEMBERS",
  WORKSPACE_BULK_CHANGE_ROLE: "WORKSPACE_BULK_CHANGE_ROLE",
  WORKSPACE_BULK_INVITE: "WORKSPACE_BULK_INVITE",
  MANAGER_VIEW_REPORTS: "MANAGER_VIEW_REPORTS",
  MANAGER_BULK_EDIT_REPORTS: "MANAGER_BULK_EDIT_REPORTS",

  // Module 3: Todos (mirror of shore)
  TODO_CREATE: "TODO_CREATE",
  TODO_VIEW_OWN: "TODO_VIEW_OWN",
  TODO_VIEW_SUBTREE: "TODO_VIEW_SUBTREE",
  TODO_VIEW_WORKSPACE: "TODO_VIEW_WORKSPACE",
  TODO_EDIT_OWN: "TODO_EDIT_OWN",
  TODO_REASSIGN: "TODO_REASSIGN",
  TODO_DELETE_OWN: "TODO_DELETE_OWN",
  TODO_TRANSITION_STATUS: "TODO_TRANSITION_STATUS",
  TODO_REORDER: "TODO_REORDER",
  INTERNAL_TODO_CREATE: "INTERNAL_TODO_CREATE",
  COMMENT_CREATE: "COMMENT_CREATE",
  COMMENT_EDIT_OWN: "COMMENT_EDIT_OWN",
  COMMENT_DELETE_OWN: "COMMENT_DELETE_OWN",
  COMMENT_DELETE_ANY: "COMMENT_DELETE_ANY",

  // -- Module 4: Feedback (mirrors shore exactly) --
  FEEDBACK_CREATE: "FEEDBACK_CREATE",
  FEEDBACK_VIEW_OWN: "FEEDBACK_VIEW_OWN",
  FEEDBACK_VIEW_SUBTREE: "FEEDBACK_VIEW_SUBTREE",
  FEEDBACK_VIEW_WORKSPACE: "FEEDBACK_VIEW_WORKSPACE",
  FEEDBACK_EDIT_OWN: "FEEDBACK_EDIT_OWN",
  FEEDBACK_DELETE_OWN: "FEEDBACK_DELETE_OWN",
  FEEDBACK_DELETE_ANY: "FEEDBACK_DELETE_ANY",
  FEEDBACK_ACKNOWLEDGE: "FEEDBACK_ACKNOWLEDGE",
  FEEDBACK_PROMOTE_ACTION_TO_TODO: "FEEDBACK_PROMOTE_ACTION_TO_TODO",
  INTERNAL_FEEDBACK_ANALYZE: "INTERNAL_FEEDBACK_ANALYZE",

  // -- Module 5: Kudos + shared Reactions primitive --
  KUDOS_CREATE: "KUDOS_CREATE",
  KUDOS_VIEW: "KUDOS_VIEW",
  KUDOS_EDIT_OWN: "KUDOS_EDIT_OWN",
  KUDOS_DELETE_OWN: "KUDOS_DELETE_OWN",
  KUDOS_DELETE_ANY: "KUDOS_DELETE_ANY",
  INTERNAL_KUDOS_ANALYZE: "INTERNAL_KUDOS_ANALYZE",
  REACTION_TOGGLE: "REACTION_TOGGLE",
  REACTION_DELETE_ANY: "REACTION_DELETE_ANY",

  // -- Module 6: One-on-Ones --
  ONE_ON_ONE_CREATE: "ONE_ON_ONE_CREATE",
  ONE_ON_ONE_VIEW_OWN: "ONE_ON_ONE_VIEW_OWN",
  ONE_ON_ONE_VIEW_SUBTREE: "ONE_ON_ONE_VIEW_SUBTREE",
  ONE_ON_ONE_VIEW_WORKSPACE: "ONE_ON_ONE_VIEW_WORKSPACE",
  ONE_ON_ONE_EDIT: "ONE_ON_ONE_EDIT",
  ONE_ON_ONE_RESCHEDULE: "ONE_ON_ONE_RESCHEDULE",
  ONE_ON_ONE_CANCEL: "ONE_ON_ONE_CANCEL",
  ONE_ON_ONE_DELETE_ANY: "ONE_ON_ONE_DELETE_ANY",
  ONE_ON_ONE_AGENDA_MANAGE: "ONE_ON_ONE_AGENDA_MANAGE",
  ONE_ON_ONE_SPAWN_ACTION_ITEM: "ONE_ON_ONE_SPAWN_ACTION_ITEM",
  INTERNAL_ONE_ON_ONE_MATERIALIZE_NEXT: "INTERNAL_ONE_ON_ONE_MATERIALIZE_NEXT",

  // -- Module 7: Notifications & Alerts --
  NOTIFICATION_VIEW_OWN: "NOTIFICATION_VIEW_OWN",
  NOTIFICATION_VIEW_UNREAD_COUNT: "NOTIFICATION_VIEW_UNREAD_COUNT",
  NOTIFICATION_MARK_READ: "NOTIFICATION_MARK_READ",
  NOTIFICATION_MARK_ALL_READ: "NOTIFICATION_MARK_ALL_READ",
  NOTIFICATION_DELETE_OWN: "NOTIFICATION_DELETE_OWN",
  NOTIFICATION_PREF_VIEW: "NOTIFICATION_PREF_VIEW",
  NOTIFICATION_PREF_EDIT: "NOTIFICATION_PREF_EDIT",
  INTERNAL_NOTIFICATION_DISPATCH: "INTERNAL_NOTIFICATION_DISPATCH",
  INTERNAL_NOTIFICATION_EMAIL_RENDER: "INTERNAL_NOTIFICATION_EMAIL_RENDER",

  // -- Module 8: Health Inputs + Pulse Surveys --
  HEALTH_VOCABULARY_VIEW: "HEALTH_VOCABULARY_VIEW",
  WORKSPACE_HEALTH_CONFIG_VIEW: "WORKSPACE_HEALTH_CONFIG_VIEW",
  WORKSPACE_HEALTH_CONFIG_EDIT: "WORKSPACE_HEALTH_CONFIG_EDIT",
  PULSE_ANSWER_REPLIES_SETTING_VIEW: "PULSE_ANSWER_REPLIES_SETTING_VIEW",
  PULSE_ANSWER_REPLIES_SETTING_MANAGE: "PULSE_ANSWER_REPLIES_SETTING_MANAGE",
  PULSE_WRITTEN_ANSWERS_VIEW: "PULSE_WRITTEN_ANSWERS_VIEW",
  PULSE_ANSWER_THREAD_VIEW: "PULSE_ANSWER_THREAD_VIEW",
  PULSE_ANSWER_THREAD_REPLY: "PULSE_ANSWER_THREAD_REPLY",
  PULSE_ANSWER_THREAD_VIEW_OWN: "PULSE_ANSWER_THREAD_VIEW_OWN",
  PULSE_ANSWER_THREAD_REPLY_OWN: "PULSE_ANSWER_THREAD_REPLY_OWN",
  PULSE_ANSWER_THREAD_REVEAL_OWN: "PULSE_ANSWER_THREAD_REVEAL_OWN",
  PULSE_SURVEY_CREATE: "PULSE_SURVEY_CREATE",
  PULSE_SURVEY_EDIT: "PULSE_SURVEY_EDIT",
  PULSE_SURVEY_ACTIVATE: "PULSE_SURVEY_ACTIVATE",
  PULSE_SURVEY_PAUSE: "PULSE_SURVEY_PAUSE",
  PULSE_SURVEY_ARCHIVE: "PULSE_SURVEY_ARCHIVE",
  PULSE_SURVEY_DELETE: "PULSE_SURVEY_DELETE",
  PULSE_SURVEY_LIST_WORKSPACE: "PULSE_SURVEY_LIST_WORKSPACE",
  PULSE_SURVEY_VIEW: "PULSE_SURVEY_VIEW",
  PULSE_SURVEY_RUN_CANCEL: "PULSE_SURVEY_RUN_CANCEL",
  PULSE_SURVEY_RUN_LIST: "PULSE_SURVEY_RUN_LIST",
  PULSE_SURVEY_INSIGHTS_VIEW: "PULSE_SURVEY_INSIGHTS_VIEW",
  PULSE_SURVEY_AI_TEST_RUN: "PULSE_SURVEY_AI_TEST_RUN",
  PULSE_SURVEY_RESPOND: "PULSE_SURVEY_RESPOND",
  PULSE_SURVEY_VIEW_OWN_HISTORY: "PULSE_SURVEY_VIEW_OWN_HISTORY",
  INTERNAL_PULSE_SURVEY_DISPATCH: "INTERNAL_PULSE_SURVEY_DISPATCH",
  INTERNAL_PULSE_AI_QUESTION_GEN: "INTERNAL_PULSE_AI_QUESTION_GEN",
  INTERNAL_PULSE_REFLECTION_EXTRACT: "INTERNAL_PULSE_REFLECTION_EXTRACT",

  // ----- Module 9, Pulse Scoring Engine -----
  PULSE_SCORE_VIEW_OWN: "PULSE_SCORE_VIEW_OWN",
  PULSE_SCORE_VIEW_MANAGER_SUBTREE: "PULSE_SCORE_VIEW_MANAGER_SUBTREE",
  PULSE_SCORE_VIEW_DEPARTMENT: "PULSE_SCORE_VIEW_DEPARTMENT",
  PULSE_SCORE_VIEW_WORKSPACE: "PULSE_SCORE_VIEW_WORKSPACE",
  INTERNAL_PULSE_SCORING_RUN: "INTERNAL_PULSE_SCORING_RUN",

  // ----- Module 10, Pulse Audience Insights -----
  PULSE_INSIGHT_VIEW_OWN: "PULSE_INSIGHT_VIEW_OWN",
  PULSE_INSIGHT_VIEW_DEPARTMENT: "PULSE_INSIGHT_VIEW_DEPARTMENT",
  PULSE_INSIGHT_VIEW_WORKSPACE: "PULSE_INSIGHT_VIEW_WORKSPACE",
  PULSE_BRIEF_VIEW_MANAGER: "PULSE_BRIEF_VIEW_MANAGER",
  PULSE_INSIGHT_DISMISS: "PULSE_INSIGHT_DISMISS",
  PULSE_INSIGHT_REVIEW: "PULSE_INSIGHT_REVIEW",
  INTERNAL_PULSE_INSIGHT_GENERATE: "INTERNAL_PULSE_INSIGHT_GENERATE",

  // ----- Module 11, Pulse Action Engine -----
  PULSE_ACTION_CREATE: "PULSE_ACTION_CREATE",
  PULSE_ACTION_VIEW_OWN: "PULSE_ACTION_VIEW_OWN",
  PULSE_ACTION_VIEW_DEPARTMENT: "PULSE_ACTION_VIEW_DEPARTMENT",
  PULSE_ACTION_VIEW_WORKSPACE: "PULSE_ACTION_VIEW_WORKSPACE",
  PULSE_ACTION_TRANSITION: "PULSE_ACTION_TRANSITION",
  PULSE_ACTION_DISMISS: "PULSE_ACTION_DISMISS",
  PULSE_ACTION_MEASURE: "PULSE_ACTION_MEASURE",
  PULSE_ACTION_CLOSE: "PULSE_ACTION_CLOSE",
  PULSE_ACTION_UPDATE_VIEW: "PULSE_ACTION_UPDATE_VIEW",
  PULSE_ACTION_ANALYTICS_VIEW: "PULSE_ACTION_ANALYTICS_VIEW",

  // ----- Module 12, Pulse Audience Dashboards -----
  PULSE_DASHBOARD_VIEW: "PULSE_DASHBOARD_VIEW",
  PULSE_DASHBOARD_VIEW_DEPARTMENT: "PULSE_DASHBOARD_VIEW_DEPARTMENT",
  PULSE_DASHBOARD_VIEW_WORKSPACE: "PULSE_DASHBOARD_VIEW_WORKSPACE",

  // ----- Module 13, Reputation Score (mirror of shore) -----
  REPUTATION_VIEW_OWN: "REPUTATION_VIEW_OWN",
  REPUTATION_VIEW_MANAGER_SUBTREE: "REPUTATION_VIEW_MANAGER_SUBTREE",
  REPUTATION_VIEW_WORKSPACE: "REPUTATION_VIEW_WORKSPACE",
  INTERNAL_REPUTATION_ANALYZE: "INTERNAL_REPUTATION_ANALYZE",
  INTERNAL_REPUTATION_SCORING_RUN: "INTERNAL_REPUTATION_SCORING_RUN",

  // ----- Module 15, Writing Assistant (mirror of shore) -----
  WRITING_ASSISTANT_GENERATE: "WRITING_ASSISTANT_GENERATE",

  // ----- Copilot (mirror of shore) -----
  COPILOT_USE: "COPILOT_USE",

  // AI usage. Mirrors shore/src/shared/auth/permission-catalog/ai-usage.ts.
  AI_USAGE_VIEW: "AI_USAGE_VIEW",

  // Billing. Mirrors shore/src/shared/auth/permission-catalog/billing.ts.
  BILLING_REDEEM_CODE: "BILLING_REDEEM_CODE",
  BILLING_VIEW_ME: "BILLING_VIEW_ME",

  // Module 16, HRMS Attendance. Mirrors
  // shore/src/shared/auth/permission-catalog/attendance.ts. Members hold only
  // the three self-scoped ones: nothing an employee holds writes a verdict.
  ATTENDANCE_CHECKIN_SELF: "ATTENDANCE_CHECKIN_SELF",
  ATTENDANCE_VIEW_OWN: "ATTENDANCE_VIEW_OWN",
  ATTENDANCE_REQUEST_CREATE: "ATTENDANCE_REQUEST_CREATE",
  ATTENDANCE_VIEW_SUBTREE: "ATTENDANCE_VIEW_SUBTREE",
  ATTENDANCE_REQUEST_DECIDE: "ATTENDANCE_REQUEST_DECIDE",
  ATTENDANCE_VIEW_WORKSPACE: "ATTENDANCE_VIEW_WORKSPACE",
  ATTENDANCE_REPORT_EXPORT: "ATTENDANCE_REPORT_EXPORT",
  ATTENDANCE_CONFIGURE: "ATTENDANCE_CONFIGURE",
  ATTENDANCE_MARK: "ATTENDANCE_MARK",
  ATTENDANCE_CHECKIN_MANAGE: "ATTENDANCE_CHECKIN_MANAGE",
  ATTENDANCE_REPROCESS: "ATTENDANCE_REPROCESS",
  // No human grant. Held only by the device ingest route, which
  // authenticates with the shared static admin key rather than a session.
  INTERNAL_ATTENDANCE_INGEST: "INTERNAL_ATTENDANCE_INGEST",

  // Module 17, HRMS Leave. Mirrors
  // shore/src/shared/auth/permission-catalog/leave.ts. Deliberately narrow at
  // the member tier for the same reason as attendance: nobody holds a
  // permission that writes a ledger row directly (allocation, an approved
  // application, accrual, expiry, a compensatory claim, or an admin
  // adjustment are the only writers).
  LEAVE_VIEW_OWN: "LEAVE_VIEW_OWN",
  LEAVE_APPLY: "LEAVE_APPLY",
  LEAVE_VIEW_OUT_TODAY: "LEAVE_VIEW_OUT_TODAY",
  LEAVE_VIEW_SUBTREE: "LEAVE_VIEW_SUBTREE",
  LEAVE_DECIDE: "LEAVE_DECIDE",
  LEAVE_APPLY_FOR_MEMBER: "LEAVE_APPLY_FOR_MEMBER",
  LEAVE_VIEW_WORKSPACE: "LEAVE_VIEW_WORKSPACE",
  LEAVE_BALANCE_VIEW_WORKSPACE: "LEAVE_BALANCE_VIEW_WORKSPACE",
  LEAVE_REPORT_EXPORT: "LEAVE_REPORT_EXPORT",
  LEAVE_CONFIGURE: "LEAVE_CONFIGURE",
  LEAVE_ALLOCATE: "LEAVE_ALLOCATE",
  LEAVE_ADJUST: "LEAVE_ADJUST",

  // hr-core (FND-010, part 2 of 4). Mirrors
  // shore/src/shared/auth/permission-catalog/hr-core.ts. No
  // EMPLOYEE_PROFILE_EDIT_SENSITIVE_OWN: a member may READ their own
  // sensitive fields but only an admin may edit sensitive fields, including
  // their own. That absence is the design, not an omission.
  EMPLOYEE_PROFILE_VIEW_OWN: "EMPLOYEE_PROFILE_VIEW_OWN",
  EMPLOYEE_PROFILE_VIEW_SUBTREE: "EMPLOYEE_PROFILE_VIEW_SUBTREE",
  EMPLOYEE_PROFILE_VIEW_WORKSPACE: "EMPLOYEE_PROFILE_VIEW_WORKSPACE",
  EMPLOYEE_PROFILE_EDIT_OWN: "EMPLOYEE_PROFILE_EDIT_OWN",
  EMPLOYEE_PROFILE_EDIT_WORKSPACE: "EMPLOYEE_PROFILE_EDIT_WORKSPACE",
  EMPLOYEE_PROFILE_VIEW_SENSITIVE_OWN: "EMPLOYEE_PROFILE_VIEW_SENSITIVE_OWN",
  /// ESS-11. Asking for a bank or PAN change, which an admin then approves.
  /// Grants no write of its own, which is why every role holds it while
  /// EDIT_SENSITIVE_OWN deliberately does not exist.
  EMPLOYEE_PROFILE_REQUEST_CHANGE_OWN: "EMPLOYEE_PROFILE_REQUEST_CHANGE_OWN",
  EMPLOYEE_PROFILE_VIEW_SENSITIVE_WORKSPACE: "EMPLOYEE_PROFILE_VIEW_SENSITIVE_WORKSPACE",
  EMPLOYEE_PROFILE_EDIT_SENSITIVE_WORKSPACE: "EMPLOYEE_PROFILE_EDIT_SENSITIVE_WORKSPACE",
  DESIGNATION_VIEW: "DESIGNATION_VIEW",
  DESIGNATION_MANAGE: "DESIGNATION_MANAGE",
  GRADE_VIEW: "GRADE_VIEW",
  GRADE_MANAGE: "GRADE_MANAGE",
  HOLIDAY_CALENDAR_VIEW: "HOLIDAY_CALENDAR_VIEW",
  HOLIDAY_CALENDAR_MANAGE: "HOLIDAY_CALENDAR_MANAGE",
  HR_SETTINGS_VIEW: "HR_SETTINGS_VIEW",
  HR_SETTINGS_MANAGE: "HR_SETTINGS_MANAGE",
  HR_IMPORT_RUN: "HR_IMPORT_RUN",
  HR_ATTACHMENT_UPLOAD: "HR_ATTACHMENT_UPLOAD",
  HR_ATTACHMENT_DOWNLOAD: "HR_ATTACHMENT_DOWNLOAD",
  HR_ATTACHMENT_DELETE_OWN: "HR_ATTACHMENT_DELETE_OWN",
  HR_ATTACHMENT_DELETE_ANY: "HR_ATTACHMENT_DELETE_ANY",
  HR_AUDIT_VIEW_OWN: "HR_AUDIT_VIEW_OWN",
  HR_AUDIT_VIEW_WORKSPACE: "HR_AUDIT_VIEW_WORKSPACE",

  // hr-approvals (FND-024). Mirrors
  // shore/src/shared/auth/permission-catalog/hr-approvals.ts. Viewing your
  // own requests, viewing a workspace's request queue, deciding (approve or
  // reject) a request, and managing which employees are configured as
  // department approvers.
  APPROVAL_VIEW_OWN: "APPROVAL_VIEW_OWN",
  APPROVAL_VIEW_WORKSPACE: "APPROVAL_VIEW_WORKSPACE",
  APPROVAL_DECIDE: "APPROVAL_DECIDE",
  APPROVAL_MANAGE_DEPARTMENT_APPROVERS: "APPROVAL_MANAGE_DEPARTMENT_APPROVERS",

  // Module 40, HRMS Payroll (PAY-002). Mirrors
  // shore/src/shared/auth/permission-catalog/payroll.ts, key for key.
  //
  // Managers hold ZERO payroll admin keys, unlike every other HR module. A
  // manager who can read their reports' salaries changes what a 1:1 feels
  // like, and nothing in the product needs it. There is deliberately no
  // `_SUBTREE` key here; the parity check greps for one.
  PAYROLL_CONFIG_VIEW: "PAYROLL_CONFIG_VIEW",
  PAYROLL_CONFIG_MANAGE: "PAYROLL_CONFIG_MANAGE",
  /// PAYXUL-FR-001. Reopen inputs_locked to open. Also unfreezes attendance
  /// and leave through the cut-off, so it is a named key. Bundled to payroll
  /// admin: locking too early is an operator mistake, not an owner incident.
  PAYROLL_CYCLE_UNLOCK: "PAYROLL_CYCLE_UNLOCK",
  SALARY_ASSIGNMENT_VIEW_OWN: "SALARY_ASSIGNMENT_VIEW_OWN",
  SALARY_ASSIGNMENT_MANAGE_WORKSPACE: "SALARY_ASSIGNMENT_MANAGE_WORKSPACE",
  PAYROLL_RUN_VIEW_WORKSPACE: "PAYROLL_RUN_VIEW_WORKSPACE",
  PAYROLL_RUN_MANAGE_WORKSPACE: "PAYROLL_RUN_MANAGE_WORKSPACE",
  SALARY_SLIP_VIEW_OWN: "SALARY_SLIP_VIEW_OWN",
  SALARY_SLIP_VIEW_WORKSPACE: "SALARY_SLIP_VIEW_WORKSPACE",
  SALARY_SLIP_MANAGE_WORKSPACE: "SALARY_SLIP_MANAGE_WORKSPACE",
  PAYROLL_BANK_EXPORT: "PAYROLL_BANK_EXPORT",
  ADDITIONAL_SALARY_VIEW_OWN: "ADDITIONAL_SALARY_VIEW_OWN",
  ADDITIONAL_SALARY_MANAGE_WORKSPACE: "ADDITIONAL_SALARY_MANAGE_WORKSPACE",
  /// PAYD-FR-003. Importing payroll inputs from a spreadsheet. Its own key
  /// rather than a reuse of ADDITIONAL_SALARY_MANAGE_WORKSPACE: one CSV writes
  /// three things (additional salary rows, holds, payment-days overrides), and
  /// the import seam allows a handler exactly one permission.
  PAYROLL_INPUT_IMPORT_RUN: "PAYROLL_INPUT_IMPORT_RUN",
  TAX_DECLARATION_SUBMIT_OWN: "TAX_DECLARATION_SUBMIT_OWN",
  TAX_DECLARATION_VIEW_OWN: "TAX_DECLARATION_VIEW_OWN",
  TAX_DECLARATION_VIEW_WORKSPACE: "TAX_DECLARATION_VIEW_WORKSPACE",
  TAX_DECLARATION_MANAGE_WORKSPACE: "TAX_DECLARATION_MANAGE_WORKSPACE",
  BENEFIT_APPLICATION_SUBMIT_OWN: "BENEFIT_APPLICATION_SUBMIT_OWN",
  BENEFIT_CLAIM_SUBMIT_OWN: "BENEFIT_CLAIM_SUBMIT_OWN",
  BENEFIT_VIEW_OWN: "BENEFIT_VIEW_OWN",
  BENEFIT_MANAGE_WORKSPACE: "BENEFIT_MANAGE_WORKSPACE",
  SALARY_WITHHOLDING_MANAGE_WORKSPACE: "SALARY_WITHHOLDING_MANAGE_WORKSPACE",
  GRATUITY_VIEW_OWN: "GRATUITY_VIEW_OWN",
  GRATUITY_MANAGE_WORKSPACE: "GRATUITY_MANAGE_WORKSPACE",
  PAYROLL_REPORT_EXPORT: "PAYROLL_REPORT_EXPORT",
  // Payroll depth. The last two are granted to named people, never bundled:
  // a lock every payroll admin can walk through is not a lock, and
  // maker-checker where one role holds both halves checks nothing.
  // Variable pay: plans, batches, approve, and Send to payroll (writes extras).
  VARIABLE_PAY_MANAGE_WORKSPACE: "VARIABLE_PAY_MANAGE_WORKSPACE",
  VARIABLE_PAY_VIEW_OWN: "VARIABLE_PAY_VIEW_OWN",
  COMPLIANCE_DOCUMENT_MANAGE_WORKSPACE: "COMPLIANCE_DOCUMENT_MANAGE_WORKSPACE",
  COMPLIANCE_DOCUMENT_VIEW_OWN: "COMPLIANCE_DOCUMENT_VIEW_OWN",
  PAYROLL_PERIOD_LOCK_OVERRIDE: "PAYROLL_PERIOD_LOCK_OVERRIDE",
  PAYROLL_RUN_APPROVE_WORKSPACE: "PAYROLL_RUN_APPROVE_WORKSPACE",
  PAYROLL_ERASURE_REQUEST_MANAGE: "PAYROLL_ERASURE_REQUEST_MANAGE",

  /// PAYD-FR-030 to FR-043 (G-04, G-05, G-12, I-13). The full and final
  /// settlement worksheet. VIEW_OWN is no longer mirror-only: it now gates
  /// `settlements/me` (which settlement is mine) and G-11's five statement
  /// routes, so it is the key the pay details settlement card gates on. Every
  /// human role holds it, payroll admins included, and the shore decides the
  /// lens; holding it is not a claim to see anybody else's.
  FNF_VIEW_OWN: "FNF_VIEW_OWN",
  FNF_MANAGE_WORKSPACE: "FNF_MANAGE_WORKSPACE",
  // -- HRMS compensation planning (CMP-001; mirror of shore permission-catalog/compensation.ts) --
  // Managers hold zero standing pay visibility: the planner lens
  // (_ASSIGNED keys) is cycle-scoped and named, never a subtree, and the
  // parity script denies COMP_.*_SUBTREE (doctrine 9.1, HRMS-CMP-003).
  COMP_CYCLE_VIEW_WORKSPACE: "COMP_CYCLE_VIEW_WORKSPACE",
  COMP_CYCLE_MANAGE: "COMP_CYCLE_MANAGE",
  COMP_BAND_VIEW_OWN: "COMP_BAND_VIEW_OWN",
  COMP_BAND_MANAGE: "COMP_BAND_MANAGE",
  COMP_BUDGET_MANAGE: "COMP_BUDGET_MANAGE",
  COMP_ALLOCATION_MANAGE: "COMP_ALLOCATION_MANAGE",
  COMP_WORKSHEET_VIEW_ASSIGNED: "COMP_WORKSHEET_VIEW_ASSIGNED",
  COMP_WORKSHEET_EDIT_ASSIGNED: "COMP_WORKSHEET_EDIT_ASSIGNED",
  COMP_WORKSHEET_REVIEW: "COMP_WORKSHEET_REVIEW",
  COMP_WORKSHEET_VIEW_WORKSPACE: "COMP_WORKSHEET_VIEW_WORKSPACE",
  COMP_LETTER_VIEW_OWN: "COMP_LETTER_VIEW_OWN",
  COMP_LETTER_VIEW_ASSIGNED: "COMP_LETTER_VIEW_ASSIGNED",
  COMP_LETTER_MANAGE: "COMP_LETTER_MANAGE",
  COMP_CONVERSATION_VIEW_OWN: "COMP_CONVERSATION_VIEW_OWN",
  COMP_CONVERSATION_MANAGE_ASSIGNED: "COMP_CONVERSATION_MANAGE_ASSIGNED",
  COMP_HISTORY_VIEW_OWN: "COMP_HISTORY_VIEW_OWN",
  COMP_HISTORY_VIEW_WORKSPACE: "COMP_HISTORY_VIEW_WORKSPACE",
  COMP_POSTING_RUN: "COMP_POSTING_RUN",
  COMP_REPORT_EXPORT: "COMP_REPORT_EXPORT",
  COMP_OFFER_VIEW_WORKSPACE: "COMP_OFFER_VIEW_WORKSPACE",
  COMP_OFFER_MANAGE_WORKSPACE: "COMP_OFFER_MANAGE_WORKSPACE",
  COMP_AI_USE: "COMP_AI_USE",
  COMP_INSIGHT_VIEW: "COMP_INSIGHT_VIEW",
  COMP_TRAINING_VIEW: "COMP_TRAINING_VIEW",
  COMP_TRAINING_MANAGE: "COMP_TRAINING_MANAGE",

  // ----- Module 14, Performance Reviews (mirror of shore) -----
  REVIEW_CYCLE_CREATE: "REVIEW_CYCLE_CREATE",
  REVIEW_CYCLE_VIEW: "REVIEW_CYCLE_VIEW",
  REVIEW_CYCLE_LIST: "REVIEW_CYCLE_LIST",
  // Participant-scoped cycle listing: held by every human so any reviewee /
  // peer / manager-of-record sees the cycles they take part in. Distinct from
  // the admin-wide REVIEW_CYCLE_LIST.
  REVIEW_CYCLE_LIST_MINE: "REVIEW_CYCLE_LIST_MINE",
  REVIEW_CYCLE_EDIT: "REVIEW_CYCLE_EDIT",
  REVIEW_CYCLE_TRANSITION: "REVIEW_CYCLE_TRANSITION",
  REVIEW_CYCLE_DELETE: "REVIEW_CYCLE_DELETE",
  REVIEW_CONFIG_VIEW: "REVIEW_CONFIG_VIEW",
  REVIEW_CONFIG_EDIT: "REVIEW_CONFIG_EDIT",
  REVIEW_CONFIG_LOCK: "REVIEW_CONFIG_LOCK",
  REVIEW_TEMPLATE_MANAGE: "REVIEW_TEMPLATE_MANAGE",
  REVIEW_PARTICIPANT_VIEW: "REVIEW_PARTICIPANT_VIEW",
  REVIEW_PARTICIPANT_MATERIALIZE: "REVIEW_PARTICIPANT_MATERIALIZE",
  REVIEW_SELF_VIEW: "REVIEW_SELF_VIEW",
  REVIEW_SELF_EDIT: "REVIEW_SELF_EDIT",
  REVIEW_SELF_SUBMIT: "REVIEW_SELF_SUBMIT",
  REVIEW_MANAGER_VIEW: "REVIEW_MANAGER_VIEW",
  REVIEW_MANAGER_EDIT: "REVIEW_MANAGER_EDIT",
  REVIEW_MANAGER_SUBMIT: "REVIEW_MANAGER_SUBMIT",
  REVIEW_MANAGER_OVERRIDE: "REVIEW_MANAGER_OVERRIDE",
  REVIEW_SIGN_OFF: "REVIEW_SIGN_OFF",
  REVIEW_HR_REVIEW: "REVIEW_HR_REVIEW",
  REVIEW_ROLLOUT_VIEW: "REVIEW_ROLLOUT_VIEW",
  REVIEW_ROLLOUT_MANAGE: "REVIEW_ROLLOUT_MANAGE",
  REVIEW_SUGGESTION_VIEW: "REVIEW_SUGGESTION_VIEW",
  REVIEW_SUGGESTION_GENERATE: "REVIEW_SUGGESTION_GENERATE",
  REVIEW_SUGGESTION_MANAGE: "REVIEW_SUGGESTION_MANAGE",
  REVIEW_PEER_MANAGE: "REVIEW_PEER_MANAGE",
  REVIEW_PEER_RESPOND: "REVIEW_PEER_RESPOND",
  REVIEW_PEER_REQUEST_SELF: "REVIEW_PEER_REQUEST_SELF",
  REVIEW_RECURRENCE_MANAGE: "REVIEW_RECURRENCE_MANAGE",
  REVIEW_APPRAISAL_SCHEDULE_MANAGE: "REVIEW_APPRAISAL_SCHEDULE_MANAGE",
  REVIEW_INSIGHTS_VIEW: "REVIEW_INSIGHTS_VIEW",

  // ----- Module: Goals & Check-ins (mirror of shore) -----
  GOAL_CREATE: "GOAL_CREATE",
  GOAL_CREATE_DEPARTMENT: "GOAL_CREATE_DEPARTMENT",
  GOAL_CREATE_WORKSPACE: "GOAL_CREATE_WORKSPACE",
  GOAL_VIEW_OWN: "GOAL_VIEW_OWN",
  GOAL_VIEW_SUBTREE: "GOAL_VIEW_SUBTREE",
  GOAL_VIEW_WORKSPACE: "GOAL_VIEW_WORKSPACE",
  GOAL_EDIT: "GOAL_EDIT",
  GOAL_DELETE: "GOAL_DELETE",
  GOAL_MANAGE_ASSIGNEES: "GOAL_MANAGE_ASSIGNEES",
  GOAL_ALIGN: "GOAL_ALIGN",
  GOAL_UPDATE_PROGRESS: "GOAL_UPDATE_PROGRESS",
  GOAL_CHECKIN_CREATE: "GOAL_CHECKIN_CREATE",
  GOAL_CHECKIN_DELETE: "GOAL_CHECKIN_DELETE",
  GOAL_RISK_CREATE: "GOAL_RISK_CREATE",
  GOAL_RISK_CLOSE: "GOAL_RISK_CLOSE",
  GOAL_RISK_UPDATE: "GOAL_RISK_UPDATE",

  // Module 30 expenses. Literal mirror of shore's
  // src/shared/auth/permission-catalog/expenses.ts. Nearly every key is
  // all-humans and narrowed by a resource policy on the server; the client
  // gates rendering only. EXPENSE_CLAIM_SANCTION is all-humans by w2-gate
  // verdict 10 and is narrowed to the live request's candidates, so a member
  // holding it still sees no sanction control on someone else's claim.
  EXPENSE_CATEGORY_VIEW: "EXPENSE_CATEGORY_VIEW",
  EXPENSE_CATEGORY_MANAGE: "EXPENSE_CATEGORY_MANAGE",
  EXPENSE_CLAIM_CREATE: "EXPENSE_CLAIM_CREATE",
  EXPENSE_CLAIM_EDIT_OWN: "EXPENSE_CLAIM_EDIT_OWN",
  EXPENSE_CLAIM_CANCEL_OWN: "EXPENSE_CLAIM_CANCEL_OWN",
  EXPENSE_CLAIM_VIEW_OWN: "EXPENSE_CLAIM_VIEW_OWN",
  EXPENSE_CLAIM_VIEW_WORKSPACE: "EXPENSE_CLAIM_VIEW_WORKSPACE",
  EXPENSE_CLAIM_SETTLE_WORKSPACE: "EXPENSE_CLAIM_SETTLE_WORKSPACE",
  EXPENSE_CLAIM_SANCTION: "EXPENSE_CLAIM_SANCTION",
  ADVANCE_CREATE: "ADVANCE_CREATE",
  ADVANCE_CANCEL_OWN: "ADVANCE_CANCEL_OWN",
  ADVANCE_VIEW_OWN: "ADVANCE_VIEW_OWN",
  ADVANCE_VIEW_WORKSPACE: "ADVANCE_VIEW_WORKSPACE",
  ADVANCE_SETTLE_WORKSPACE: "ADVANCE_SETTLE_WORKSPACE",
  TRAVEL_REQUEST_CREATE: "TRAVEL_REQUEST_CREATE",
  TRAVEL_REQUEST_CANCEL_OWN: "TRAVEL_REQUEST_CANCEL_OWN",
  TRAVEL_REQUEST_VIEW_OWN: "TRAVEL_REQUEST_VIEW_OWN",
  TRAVEL_REQUEST_VIEW_WORKSPACE: "TRAVEL_REQUEST_VIEW_WORKSPACE",
  EXPENSE_REPORT_EXPORT: "EXPENSE_REPORT_EXPORT",

  PUBLIC: "PUBLIC",

  // recruitment (REC-000). Mirror of shore permission-catalog/recruitment.ts.
  // Four all-human keys are the referral and interviewer surface. Managers add
  // the three requisition `_OWN` keys only: applicant rows are PII about people
  // who have not joined, so the pipeline stays admin. The PUBLIC apply route
  // (REC-012) adds no key here; it ships under the PUBLIC sentinel.
  STAFFING_PLAN_VIEW: "STAFFING_PLAN_VIEW",
  STAFFING_PLAN_MANAGE: "STAFFING_PLAN_MANAGE",
  JOB_REQUISITION_CREATE: "JOB_REQUISITION_CREATE",
  JOB_REQUISITION_VIEW_OWN: "JOB_REQUISITION_VIEW_OWN",
  JOB_REQUISITION_CANCEL_OWN: "JOB_REQUISITION_CANCEL_OWN",
  JOB_REQUISITION_VIEW_WORKSPACE: "JOB_REQUISITION_VIEW_WORKSPACE",
  JOB_OPENING_VIEW: "JOB_OPENING_VIEW",
  JOB_OPENING_MANAGE: "JOB_OPENING_MANAGE",
  APPLICANT_VIEW_WORKSPACE: "APPLICANT_VIEW_WORKSPACE",
  APPLICANT_MANAGE_WORKSPACE: "APPLICANT_MANAGE_WORKSPACE",
  APPLICANT_HIRE_CONVERT_WORKSPACE: "APPLICANT_HIRE_CONVERT_WORKSPACE",
  INTERVIEW_SCHEDULE_WORKSPACE: "INTERVIEW_SCHEDULE_WORKSPACE",
  INTERVIEW_VIEW_OWN: "INTERVIEW_VIEW_OWN",
  INTERVIEW_FEEDBACK_SUBMIT_OWN: "INTERVIEW_FEEDBACK_SUBMIT_OWN",
  JOB_OFFER_VIEW_WORKSPACE: "JOB_OFFER_VIEW_WORKSPACE",
  JOB_OFFER_MANAGE_WORKSPACE: "JOB_OFFER_MANAGE_WORKSPACE",
  REFERRAL_CREATE: "REFERRAL_CREATE",
  REFERRAL_VIEW_OWN: "REFERRAL_VIEW_OWN",
  REFERRAL_MANAGE_WORKSPACE: "REFERRAL_MANAGE_WORKSPACE",
  APPOINTMENT_LETTER_MANAGE_WORKSPACE: "APPOINTMENT_LETTER_MANAGE_WORKSPACE",
  RECRUITMENT_REPORT_EXPORT: "RECRUITMENT_REPORT_EXPORT",
} as const;
export type Permission = (typeof Permission)[keyof typeof Permission];

export const WorkspaceRole = {
  ADMIN: "admin",
  MANAGER: "manager",
  MEMBER: "member",
} as const;
export type WorkspaceRole = (typeof WorkspaceRole)[keyof typeof WorkspaceRole];

export const AccountRole = {
  OWNER: "owner",
  ADMIN: "admin",
} as const;
export type AccountRole = (typeof AccountRole)[keyof typeof AccountRole];

// M3 (Todos), broadly granted, narrowly gated by per-resource policy on the
// server. SUBTREE is Manager+; WORKSPACE / COMMENT_DELETE_ANY is Admin+;
// INTERNAL_TODO_CREATE has no human grant.
const M3_TODO_ALL_HUMANS_PERMS = [
  Permission.TODO_CREATE,
  Permission.TODO_VIEW_OWN,
  Permission.TODO_EDIT_OWN,
  Permission.TODO_REASSIGN,
  Permission.TODO_DELETE_OWN,
  Permission.TODO_TRANSITION_STATUS,
  Permission.TODO_REORDER,
  Permission.COMMENT_CREATE,
  Permission.COMMENT_EDIT_OWN,
  Permission.COMMENT_DELETE_OWN,
] as const;
const M3_ADMIN_PERMS = [
  ...M3_TODO_ALL_HUMANS_PERMS,
  Permission.TODO_VIEW_SUBTREE,
  Permission.TODO_VIEW_WORKSPACE,
  Permission.COMMENT_DELETE_ANY,
] as const;
const M3_MANAGER_PERMS = [...M3_TODO_ALL_HUMANS_PERMS, Permission.TODO_VIEW_SUBTREE] as const;
const M3_MEMBER_PERMS = [...M3_TODO_ALL_HUMANS_PERMS] as const;

// Module 4, Feedback. Mirrors shore role-matrix.ts.
const M4_FEEDBACK_ALL_HUMANS_PERMS = [
  Permission.FEEDBACK_CREATE,
  Permission.FEEDBACK_VIEW_OWN,
  Permission.FEEDBACK_EDIT_OWN,
  Permission.FEEDBACK_DELETE_OWN,
  Permission.FEEDBACK_ACKNOWLEDGE,
  Permission.FEEDBACK_PROMOTE_ACTION_TO_TODO,
] as const;
const M4_ADMIN_PERMS = [
  ...M4_FEEDBACK_ALL_HUMANS_PERMS,
  Permission.FEEDBACK_VIEW_SUBTREE,
  Permission.FEEDBACK_VIEW_WORKSPACE,
  Permission.FEEDBACK_DELETE_ANY,
] as const;
const M4_MANAGER_PERMS = [
  ...M4_FEEDBACK_ALL_HUMANS_PERMS,
  Permission.FEEDBACK_VIEW_SUBTREE,
] as const;
const M4_MEMBER_PERMS = [...M4_FEEDBACK_ALL_HUMANS_PERMS] as const;

// Module 5, Kudos + shared Reactions primitive.
const M5_KUDOS_ALL_HUMANS_PERMS = [
  Permission.KUDOS_CREATE,
  Permission.KUDOS_VIEW,
  Permission.KUDOS_EDIT_OWN,
  Permission.KUDOS_DELETE_OWN,
  Permission.REACTION_TOGGLE,
] as const;
const M5_ADMIN_PERMS = [
  ...M5_KUDOS_ALL_HUMANS_PERMS,
  Permission.KUDOS_DELETE_ANY,
  Permission.REACTION_DELETE_ANY,
] as const;
const M5_MANAGER_PERMS = [...M5_KUDOS_ALL_HUMANS_PERMS] as const;
const M5_MEMBER_PERMS = [...M5_KUDOS_ALL_HUMANS_PERMS] as const;

// Module 6, One-on-Ones.
const M6_ONE_ON_ONE_ALL_HUMANS_PERMS = [
  Permission.ONE_ON_ONE_CREATE,
  Permission.ONE_ON_ONE_VIEW_OWN,
  Permission.ONE_ON_ONE_EDIT,
  Permission.ONE_ON_ONE_RESCHEDULE,
  Permission.ONE_ON_ONE_CANCEL,
  Permission.ONE_ON_ONE_AGENDA_MANAGE,
  Permission.ONE_ON_ONE_SPAWN_ACTION_ITEM,
] as const;
const M6_ADMIN_PERMS = [
  ...M6_ONE_ON_ONE_ALL_HUMANS_PERMS,
  Permission.ONE_ON_ONE_VIEW_SUBTREE,
  Permission.ONE_ON_ONE_VIEW_WORKSPACE,
  Permission.ONE_ON_ONE_DELETE_ANY,
] as const;
const M6_MANAGER_PERMS = [
  ...M6_ONE_ON_ONE_ALL_HUMANS_PERMS,
  Permission.ONE_ON_ONE_VIEW_SUBTREE,
] as const;
const M6_MEMBER_PERMS = [...M6_ONE_ON_ONE_ALL_HUMANS_PERMS] as const;

// M7, Notifications & Alerts. Every human role gets the 7 inbox + prefs
// perms. Only admin tiers see the delivery audit. INTERNAL_* never granted.
const M7_NOTIFICATION_ALL_HUMANS_PERMS = [
  Permission.NOTIFICATION_VIEW_OWN,
  Permission.NOTIFICATION_VIEW_UNREAD_COUNT,
  Permission.NOTIFICATION_MARK_READ,
  Permission.NOTIFICATION_MARK_ALL_READ,
  Permission.NOTIFICATION_DELETE_OWN,
  Permission.NOTIFICATION_PREF_VIEW,
  Permission.NOTIFICATION_PREF_EDIT,
] as const;
const M7_ADMIN_PERMS = [...M7_NOTIFICATION_ALL_HUMANS_PERMS] as const;
const M7_MEMBER_PERMS = [...M7_NOTIFICATION_ALL_HUMANS_PERMS] as const;

// M8, every human reads vocabulary and responds to deliveries. Admins (and
// account admin/owner) own survey CRUD + run management + AI test-run +
// safety queue + workspace health-config.
const M8_ALL_HUMANS_PERMS = [
  Permission.HEALTH_VOCABULARY_VIEW,
  Permission.PULSE_SURVEY_RESPOND,
  Permission.PULSE_SURVEY_VIEW_OWN_HISTORY,
  Permission.PULSE_ANSWER_THREAD_VIEW_OWN,
  Permission.PULSE_ANSWER_THREAD_REPLY_OWN,
  Permission.PULSE_ANSWER_THREAD_REVEAL_OWN,
] as const;
const M8_ADMIN_PERMS = [
  ...M8_ALL_HUMANS_PERMS,
  Permission.WORKSPACE_HEALTH_CONFIG_VIEW,
  Permission.WORKSPACE_HEALTH_CONFIG_EDIT,
  Permission.PULSE_ANSWER_REPLIES_SETTING_VIEW,
  Permission.PULSE_WRITTEN_ANSWERS_VIEW,
  Permission.PULSE_ANSWER_THREAD_VIEW,
  Permission.PULSE_ANSWER_THREAD_REPLY,
  Permission.PULSE_SURVEY_CREATE,
  Permission.PULSE_SURVEY_EDIT,
  Permission.PULSE_SURVEY_ACTIVATE,
  Permission.PULSE_SURVEY_PAUSE,
  Permission.PULSE_SURVEY_ARCHIVE,
  Permission.PULSE_SURVEY_DELETE,
  Permission.PULSE_SURVEY_LIST_WORKSPACE,
  Permission.PULSE_SURVEY_VIEW,
  Permission.PULSE_SURVEY_RUN_CANCEL,
  Permission.PULSE_SURVEY_RUN_LIST,
  Permission.PULSE_SURVEY_INSIGHTS_VIEW,
  Permission.PULSE_SURVEY_AI_TEST_RUN,
] as const;
// Account owners and admins only, not workspace admins.
const M8_ACCOUNT_ADMIN_PERMS = [Permission.PULSE_ANSWER_REPLIES_SETTING_MANAGE] as const;
const M8_MEMBER_PERMS = [...M8_ALL_HUMANS_PERMS] as const;
// Managers create, edit, delete, and run surveys scoped to their own subtree
// (the shore service layer enforces both the own-survey scoping and the subtree
// audience, on create and on edit); they cannot pause, archive, cancel runs,
// use the AI test-run, or list the whole workspace.
const M8_MANAGER_PERMS = [
  ...M8_ALL_HUMANS_PERMS,
  Permission.PULSE_SURVEY_CREATE,
  Permission.PULSE_SURVEY_EDIT,
  Permission.PULSE_SURVEY_DELETE,
  Permission.PULSE_SURVEY_VIEW,
  Permission.PULSE_SURVEY_ACTIVATE,
  Permission.PULSE_SURVEY_RUN_LIST,
  Permission.PULSE_SURVEY_INSIGHTS_VIEW,
] as const;

// Module 9, Pulse Scoring Engine. Mirror of shore role-matrix.ts.
const M9_ALL_HUMANS_PERMS = [Permission.PULSE_SCORE_VIEW_OWN] as const;
const M9_ADMIN_PERMS = [
  ...M9_ALL_HUMANS_PERMS,
  Permission.PULSE_SCORE_VIEW_MANAGER_SUBTREE,
  Permission.PULSE_SCORE_VIEW_DEPARTMENT,
  Permission.PULSE_SCORE_VIEW_WORKSPACE,
] as const;
const M9_MANAGER_PERMS = [
  ...M9_ALL_HUMANS_PERMS,
  Permission.PULSE_SCORE_VIEW_MANAGER_SUBTREE,
] as const;
const M9_MEMBER_PERMS = [...M9_ALL_HUMANS_PERMS] as const;

// Module 10, Pulse Audience Insights. Mirror of shore role-matrix.
const M10_ALL_HUMANS_PERMS = [
  Permission.PULSE_INSIGHT_VIEW_OWN,
  Permission.PULSE_INSIGHT_DISMISS,
] as const;
const M10_ADMIN_PERMS = [
  ...M10_ALL_HUMANS_PERMS,
  Permission.PULSE_INSIGHT_VIEW_DEPARTMENT,
  Permission.PULSE_INSIGHT_VIEW_WORKSPACE,
  Permission.PULSE_BRIEF_VIEW_MANAGER,
  Permission.PULSE_INSIGHT_REVIEW,
] as const;
const M10_MANAGER_PERMS = [
  ...M10_ALL_HUMANS_PERMS,
  Permission.PULSE_INSIGHT_VIEW_DEPARTMENT,
  Permission.PULSE_BRIEF_VIEW_MANAGER,
] as const;
const M10_MEMBER_PERMS = [...M10_ALL_HUMANS_PERMS] as const;

// Module 11, Pulse Action Engine. Mirror of shore role-matrix.
const M11_ALL_HUMANS_PERMS = [
  Permission.PULSE_ACTION_VIEW_OWN,
  Permission.PULSE_ACTION_UPDATE_VIEW,
] as const;
const M11_ADMIN_PERMS = [
  ...M11_ALL_HUMANS_PERMS,
  Permission.PULSE_ACTION_CREATE,
  Permission.PULSE_ACTION_VIEW_DEPARTMENT,
  Permission.PULSE_ACTION_VIEW_WORKSPACE,
  Permission.PULSE_ACTION_TRANSITION,
  Permission.PULSE_ACTION_DISMISS,
  Permission.PULSE_ACTION_MEASURE,
  Permission.PULSE_ACTION_CLOSE,
  Permission.PULSE_ACTION_ANALYTICS_VIEW,
] as const;
const M11_MANAGER_PERMS = [
  ...M11_ALL_HUMANS_PERMS,
  Permission.PULSE_ACTION_CREATE,
  Permission.PULSE_ACTION_VIEW_DEPARTMENT,
  Permission.PULSE_ACTION_TRANSITION,
  Permission.PULSE_ACTION_MEASURE,
] as const;
const M11_MEMBER_PERMS = [...M11_ALL_HUMANS_PERMS] as const;

// Module 12, Pulse Audience Dashboards. Mirror of shore role-matrix.
const M12_ALL_HUMANS_PERMS = [Permission.PULSE_DASHBOARD_VIEW] as const;
const M12_ADMIN_PERMS = [
  ...M12_ALL_HUMANS_PERMS,
  Permission.PULSE_DASHBOARD_VIEW_DEPARTMENT,
  Permission.PULSE_DASHBOARD_VIEW_WORKSPACE,
] as const;
const M12_MANAGER_PERMS = [
  ...M12_ALL_HUMANS_PERMS,
  Permission.PULSE_DASHBOARD_VIEW_DEPARTMENT,
] as const;
const M12_MEMBER_PERMS = [...M12_ALL_HUMANS_PERMS] as const;

// Module 13, Reputation Score. Mirror of shore role-matrix. Read-only
// surface, no config / scoring-run perms.
const M13_ALL_HUMANS_PERMS = [Permission.REPUTATION_VIEW_OWN] as const;
const M13_ADMIN_PERMS = [
  ...M13_ALL_HUMANS_PERMS,
  Permission.REPUTATION_VIEW_MANAGER_SUBTREE,
  Permission.REPUTATION_VIEW_WORKSPACE,
] as const;
const M13_MANAGER_PERMS = [
  ...M13_ALL_HUMANS_PERMS,
  Permission.REPUTATION_VIEW_MANAGER_SUBTREE,
] as const;
const M13_MEMBER_PERMS = [...M13_ALL_HUMANS_PERMS] as const;

// Module 14, Performance Reviews. Mirror of shore role-matrix. Every human
// fills their own review + AI suggestions + peer responses; managers add the
// manager-review, peer management, and rollout; admins add cycle + config +
// participant + sign-off + HR + recurrence.
const M14_ALL_HUMANS_PERMS = [
  Permission.REVIEW_CYCLE_LIST_MINE,
  Permission.REVIEW_SELF_VIEW,
  Permission.REVIEW_SELF_EDIT,
  Permission.REVIEW_SELF_SUBMIT,
  Permission.REVIEW_SUGGESTION_VIEW,
  Permission.REVIEW_SUGGESTION_GENERATE,
  Permission.REVIEW_SUGGESTION_MANAGE,
  Permission.REVIEW_PEER_RESPOND,
  Permission.REVIEW_PEER_REQUEST_SELF,
] as const;
const M14_MANAGER_EXTRA_PERMS = [
  Permission.REVIEW_MANAGER_VIEW,
  Permission.REVIEW_MANAGER_EDIT,
  Permission.REVIEW_MANAGER_SUBMIT,
  Permission.REVIEW_ROLLOUT_VIEW,
  Permission.REVIEW_ROLLOUT_MANAGE,
  Permission.REVIEW_PEER_MANAGE,
] as const;
const M14_ADMIN_PERMS = [
  ...M14_ALL_HUMANS_PERMS,
  ...M14_MANAGER_EXTRA_PERMS,
  Permission.REVIEW_CYCLE_CREATE,
  Permission.REVIEW_CYCLE_VIEW,
  Permission.REVIEW_CYCLE_LIST,
  Permission.REVIEW_CYCLE_EDIT,
  Permission.REVIEW_CYCLE_TRANSITION,
  Permission.REVIEW_CYCLE_DELETE,
  Permission.REVIEW_CONFIG_VIEW,
  Permission.REVIEW_CONFIG_EDIT,
  Permission.REVIEW_CONFIG_LOCK,
  Permission.REVIEW_TEMPLATE_MANAGE,
  Permission.REVIEW_PARTICIPANT_VIEW,
  Permission.REVIEW_PARTICIPANT_MATERIALIZE,
  Permission.REVIEW_MANAGER_OVERRIDE,
  Permission.REVIEW_SIGN_OFF,
  Permission.REVIEW_HR_REVIEW,
  Permission.REVIEW_RECURRENCE_MANAGE,
  Permission.REVIEW_APPRAISAL_SCHEDULE_MANAGE,
  Permission.REVIEW_INSIGHTS_VIEW,
] as const;
const M14_MANAGER_PERMS = [...M14_ALL_HUMANS_PERMS, ...M14_MANAGER_EXTRA_PERMS] as const;
const M14_MEMBER_PERMS = [...M14_ALL_HUMANS_PERMS] as const;

// Module Goals & Check-ins. Mirror of shore role-permission-arrays.ts
// (M13_GOALS_*). Every human can create/edit/own goals + check-ins; managers
// gain department-create + subtree view; admins add workspace-create + view.
const MGOALS_ALL_HUMANS_PERMS = [
  Permission.GOAL_CREATE,
  Permission.GOAL_VIEW_OWN,
  Permission.GOAL_EDIT,
  Permission.GOAL_DELETE,
  Permission.GOAL_MANAGE_ASSIGNEES,
  Permission.GOAL_ALIGN,
  Permission.GOAL_UPDATE_PROGRESS,
  Permission.GOAL_CHECKIN_CREATE,
  Permission.GOAL_CHECKIN_DELETE,
  Permission.GOAL_RISK_CREATE,
  Permission.GOAL_RISK_CLOSE,
  Permission.GOAL_RISK_UPDATE,
] as const;
const MGOALS_ADMIN_PERMS = [
  ...MGOALS_ALL_HUMANS_PERMS,
  Permission.GOAL_CREATE_DEPARTMENT,
  Permission.GOAL_CREATE_WORKSPACE,
  Permission.GOAL_VIEW_SUBTREE,
  Permission.GOAL_VIEW_WORKSPACE,
] as const;
const MGOALS_MANAGER_PERMS = [
  ...MGOALS_ALL_HUMANS_PERMS,
  Permission.GOAL_CREATE_DEPARTMENT,
  Permission.GOAL_VIEW_SUBTREE,
] as const;
const MGOALS_MEMBER_PERMS = [...MGOALS_ALL_HUMANS_PERMS] as const;

// Module 15, Writing Assistant (mirror of shore role-matrix.ts).
const M15_WRITING_ASSISTANT_ALL_HUMANS_PERMS = [Permission.WRITING_ASSISTANT_GENERATE] as const;

// Copilot access. All five human role sets can use the assistant.
const COPILOT_ALL_HUMANS_PERMS = [Permission.COPILOT_USE] as const;

// Module 16, HRMS Attendance. Deliberately narrow at the member tier: an
// employee writes evidence and asks for corrections, and holds nothing that
// writes a verdict (stories GUARD-3).
const M16_ATTENDANCE_ALL_HUMANS_PERMS = [
  Permission.ATTENDANCE_CHECKIN_SELF,
  Permission.ATTENDANCE_VIEW_OWN,
  Permission.ATTENDANCE_REQUEST_CREATE,
] as const;
const M16_MEMBER_PERMS = [...M16_ATTENDANCE_ALL_HUMANS_PERMS] as const;
const M16_MANAGER_PERMS = [
  ...M16_ATTENDANCE_ALL_HUMANS_PERMS,
  Permission.ATTENDANCE_VIEW_SUBTREE,
  Permission.ATTENDANCE_REQUEST_DECIDE,
] as const;
const M16_ADMIN_PERMS = [
  ...M16_MANAGER_PERMS,
  Permission.ATTENDANCE_VIEW_WORKSPACE,
  Permission.ATTENDANCE_REPORT_EXPORT,
  Permission.ATTENDANCE_CONFIGURE,
  Permission.ATTENDANCE_MARK,
  Permission.ATTENDANCE_CHECKIN_MANAGE,
  Permission.ATTENDANCE_REPROCESS,
] as const;

// Module 17, HRMS Leave. Mirror of shore role-permission-arrays.leave.ts
// (M17_MEMBER / M17_MANAGER / M17_ADMIN). Same narrow-at-member-tier shape as
// attendance, and for the same reason.
const M17_LEAVE_ALL_HUMANS_PERMS = [
  Permission.LEAVE_VIEW_OWN,
  Permission.LEAVE_APPLY,
  Permission.LEAVE_VIEW_OUT_TODAY,
] as const;
const M17_MEMBER_PERMS = [...M17_LEAVE_ALL_HUMANS_PERMS] as const;
const M17_MANAGER_PERMS = [
  ...M17_LEAVE_ALL_HUMANS_PERMS,
  Permission.LEAVE_VIEW_SUBTREE,
  Permission.LEAVE_DECIDE,
  Permission.LEAVE_APPLY_FOR_MEMBER,
] as const;
const M17_ADMIN_PERMS = [
  ...M17_MANAGER_PERMS,
  Permission.LEAVE_VIEW_WORKSPACE,
  Permission.LEAVE_BALANCE_VIEW_WORKSPACE,
  Permission.LEAVE_REPORT_EXPORT,
  Permission.LEAVE_CONFIGURE,
  Permission.LEAVE_ALLOCATE,
  Permission.LEAVE_ADJUST,
] as const;

// hr-core (FND-010, part 2 of 4). Mirror of shore
// role-permission-arrays.hrms.ts (MHR_HR_CORE_*). Broadly held profile
// self-service; manager adds subtree visibility; admin adds workspace
// lenses, catalog management, HR settings, imports, and the workspace-wide
// attachment + audit lenses.
const MHR_HR_CORE_ALL_HUMANS_PERMS = [
  Permission.EMPLOYEE_PROFILE_VIEW_OWN,
  Permission.EMPLOYEE_PROFILE_EDIT_OWN,
  Permission.EMPLOYEE_PROFILE_VIEW_SENSITIVE_OWN,
  Permission.EMPLOYEE_PROFILE_REQUEST_CHANGE_OWN,
  Permission.DESIGNATION_VIEW,
  Permission.GRADE_VIEW,
  Permission.HOLIDAY_CALENDAR_VIEW,
  Permission.HR_ATTACHMENT_UPLOAD,
  Permission.HR_ATTACHMENT_DOWNLOAD,
  Permission.HR_ATTACHMENT_DELETE_OWN,
  Permission.HR_AUDIT_VIEW_OWN,
] as const;
const MHR_HR_CORE_MEMBER_PERMS = [...MHR_HR_CORE_ALL_HUMANS_PERMS] as const;
const MHR_HR_CORE_MANAGER_PERMS = [
  ...MHR_HR_CORE_ALL_HUMANS_PERMS,
  Permission.EMPLOYEE_PROFILE_VIEW_SUBTREE,
] as const;
const MHR_HR_CORE_ADMIN_PERMS = [
  ...MHR_HR_CORE_MANAGER_PERMS,
  Permission.EMPLOYEE_PROFILE_VIEW_WORKSPACE,
  Permission.EMPLOYEE_PROFILE_EDIT_WORKSPACE,
  Permission.EMPLOYEE_PROFILE_VIEW_SENSITIVE_WORKSPACE,
  Permission.EMPLOYEE_PROFILE_EDIT_SENSITIVE_WORKSPACE,
  Permission.DESIGNATION_MANAGE,
  Permission.GRADE_MANAGE,
  Permission.HOLIDAY_CALENDAR_MANAGE,
  Permission.HR_SETTINGS_VIEW,
  Permission.HR_SETTINGS_MANAGE,
  Permission.HR_IMPORT_RUN,
  Permission.HR_ATTACHMENT_DELETE_ANY,
  Permission.HR_AUDIT_VIEW_WORKSPACE,
] as const;

// hr-approvals (FND-024). Mirror of shore role-permission-arrays.hrms.ts
// (MHR_HR_APPROVALS_*). Every human views their own requests and can decide
// on the ones routed to them; admin adds workspace-wide visibility and
// approver configuration.
const MHR_HR_APPROVALS_ALL_HUMANS_PERMS = [
  Permission.APPROVAL_VIEW_OWN,
  Permission.APPROVAL_DECIDE,
] as const;
const MHR_HR_APPROVALS_MEMBER_PERMS = [...MHR_HR_APPROVALS_ALL_HUMANS_PERMS] as const;
// Deliberately identical to ALL_HUMANS_PERMS today: this is a named seam so
// a later story can widen the manager tier without touching five role sets.
// Do not simplify it away by inlining ALL_HUMANS_PERMS at manager call sites.
const MHR_HR_APPROVALS_MANAGER_PERMS = [...MHR_HR_APPROVALS_ALL_HUMANS_PERMS] as const;
const MHR_HR_APPROVALS_ADMIN_PERMS = [
  ...MHR_HR_APPROVALS_MANAGER_PERMS,
  Permission.APPROVAL_VIEW_WORKSPACE,
  Permission.APPROVAL_MANAGE_DEPARTMENT_APPROVERS,
] as const;

// payroll (PAY-002). Mirror of shore role-permission-arrays.hrms.ts
// (MHR_PAYROLL_*). Nine `_OWN` keys for every human, fifteen admin keys on
// top. The manager tier is deliberately identical to all-humans: there is no
// team pay lens, by design, not by omission.
const MHR_PAYROLL_ALL_HUMANS_PERMS = [
  Permission.SALARY_ASSIGNMENT_VIEW_OWN,
  Permission.SALARY_SLIP_VIEW_OWN,
  Permission.ADDITIONAL_SALARY_VIEW_OWN,
  Permission.TAX_DECLARATION_SUBMIT_OWN,
  Permission.TAX_DECLARATION_VIEW_OWN,
  Permission.BENEFIT_APPLICATION_SUBMIT_OWN,
  Permission.BENEFIT_CLAIM_SUBMIT_OWN,
  Permission.BENEFIT_VIEW_OWN,
  Permission.GRATUITY_VIEW_OWN,
  Permission.VARIABLE_PAY_VIEW_OWN,
  Permission.COMPLIANCE_DOCUMENT_VIEW_OWN,
  // G-04, G-05. Own settlement statement, same shape as own payslip.
  Permission.FNF_VIEW_OWN,
] as const;
const MHR_PAYROLL_MEMBER_PERMS = [...MHR_PAYROLL_ALL_HUMANS_PERMS] as const;
const MHR_PAYROLL_MANAGER_PERMS = [...MHR_PAYROLL_ALL_HUMANS_PERMS] as const;
const MHR_PAYROLL_ADMIN_PERMS = [
  ...MHR_PAYROLL_MANAGER_PERMS,
  Permission.PAYROLL_CONFIG_VIEW,
  Permission.PAYROLL_CONFIG_MANAGE,
  Permission.PAYROLL_CYCLE_UNLOCK,
  Permission.SALARY_ASSIGNMENT_MANAGE_WORKSPACE,
  Permission.PAYROLL_RUN_VIEW_WORKSPACE,
  Permission.PAYROLL_RUN_MANAGE_WORKSPACE,
  Permission.SALARY_SLIP_VIEW_WORKSPACE,
  Permission.SALARY_SLIP_MANAGE_WORKSPACE,
  Permission.PAYROLL_BANK_EXPORT,
  Permission.ADDITIONAL_SALARY_MANAGE_WORKSPACE,
  // PAYD-FR-003. A payroll admin already holds all three keys the payroll CSV
  // writes with, so bundling the import key grants nothing new; it exists so
  // the import can be taken away without taking away the pages it mirrors.
  Permission.PAYROLL_INPUT_IMPORT_RUN,
  Permission.TAX_DECLARATION_VIEW_WORKSPACE,
  Permission.TAX_DECLARATION_MANAGE_WORKSPACE,
  Permission.BENEFIT_MANAGE_WORKSPACE,
  Permission.SALARY_WITHHOLDING_MANAGE_WORKSPACE,
  Permission.GRATUITY_MANAGE_WORKSPACE,
  Permission.PAYROLL_REPORT_EXPORT,
  Permission.VARIABLE_PAY_MANAGE_WORKSPACE,
  Permission.COMPLIANCE_DOCUMENT_MANAGE_WORKSPACE,
  // PAYD-FR-092. Maker-checker needs an approver who is not the submitter AND
  // holds this key. It used to be held by nobody, which made the rule
  // unsatisfiable rather than strict: every caller failed the key check, so no
  // batch and no payment file could be released by anyone. Payroll admins hold
  // it, and so does the account owner, so the rule that survives is "a
  // different payroll admin". Owner-only was considered and rejected:
  // releasing payroll happens monthly on a deadline, and one holder is a
  // single point of failure rather than a safeguard.
  Permission.PAYROLL_RUN_APPROVE_WORKSPACE,
  // G-04, G-05. Build and finalize the settlement worksheet.
  Permission.FNF_MANAGE_WORKSPACE,
] as const;

// compensation (HRMS-CMP-001). Mirror of shore role-permission-arrays.hrms.ts
// (MHR_COMPENSATION_*). Twelve all-humans keys (own reads plus the assigned
// planner lens), thirteen admin keys on top. The manager tier is deliberately
// identical to all-humans: there is no team pay lens, by design (doctrine 9.1).
const MHR_COMPENSATION_ALL_HUMANS_PERMS = [
  Permission.COMP_BAND_VIEW_OWN,
  Permission.COMP_WORKSHEET_VIEW_ASSIGNED,
  Permission.COMP_WORKSHEET_EDIT_ASSIGNED,
  Permission.COMP_WORKSHEET_REVIEW,
  Permission.COMP_LETTER_VIEW_OWN,
  Permission.COMP_LETTER_VIEW_ASSIGNED,
  Permission.COMP_CONVERSATION_VIEW_OWN,
  Permission.COMP_CONVERSATION_MANAGE_ASSIGNED,
  Permission.COMP_HISTORY_VIEW_OWN,
  Permission.COMP_AI_USE,
  Permission.COMP_INSIGHT_VIEW,
  Permission.COMP_TRAINING_VIEW,
] as const;
const MHR_COMPENSATION_MEMBER_PERMS = [...MHR_COMPENSATION_ALL_HUMANS_PERMS] as const;

// Module 30 expenses (MHR_EXPENSES_*). Mirrors shore's
// role-permission-arrays.hrms.ts. Twelve all-humans keys, seven admin, and a
// manager who gains nothing: approving is the approval engine's job, decided
// per request, not a role the matrix can grant.
const MHR_EXPENSES_ALL_HUMANS_PERMS = [
  Permission.EXPENSE_CATEGORY_VIEW,
  Permission.EXPENSE_CLAIM_CREATE,
  Permission.EXPENSE_CLAIM_EDIT_OWN,
  Permission.EXPENSE_CLAIM_CANCEL_OWN,
  Permission.EXPENSE_CLAIM_VIEW_OWN,
  Permission.EXPENSE_CLAIM_SANCTION,
  Permission.ADVANCE_CREATE,
  Permission.ADVANCE_CANCEL_OWN,
  Permission.ADVANCE_VIEW_OWN,
  Permission.TRAVEL_REQUEST_CREATE,
  Permission.TRAVEL_REQUEST_CANCEL_OWN,
  Permission.TRAVEL_REQUEST_VIEW_OWN,
] as const;
const MHR_EXPENSES_MEMBER_PERMS = [...MHR_EXPENSES_ALL_HUMANS_PERMS] as const;
const MHR_EXPENSES_MANAGER_PERMS = [...MHR_EXPENSES_ALL_HUMANS_PERMS] as const;
const MHR_EXPENSES_ADMIN_PERMS = [
  ...MHR_EXPENSES_MANAGER_PERMS,
  Permission.EXPENSE_CATEGORY_MANAGE,
  Permission.EXPENSE_CLAIM_VIEW_WORKSPACE,
  Permission.EXPENSE_CLAIM_SETTLE_WORKSPACE,
  Permission.ADVANCE_VIEW_WORKSPACE,
  Permission.ADVANCE_SETTLE_WORKSPACE,
  Permission.TRAVEL_REQUEST_VIEW_WORKSPACE,
  Permission.EXPENSE_REPORT_EXPORT,
] as const;
const MHR_COMPENSATION_MANAGER_PERMS = [...MHR_COMPENSATION_ALL_HUMANS_PERMS] as const;
const MHR_COMPENSATION_ADMIN_PERMS = [
  ...MHR_COMPENSATION_MANAGER_PERMS,
  ...MHR_EXPENSES_MANAGER_PERMS,
  Permission.COMP_CYCLE_VIEW_WORKSPACE,
  Permission.COMP_CYCLE_MANAGE,
  Permission.COMP_BAND_MANAGE,
  Permission.COMP_BUDGET_MANAGE,
  Permission.COMP_ALLOCATION_MANAGE,
  Permission.COMP_WORKSHEET_VIEW_WORKSPACE,
  Permission.COMP_LETTER_MANAGE,
  Permission.COMP_HISTORY_VIEW_WORKSPACE,
  Permission.COMP_POSTING_RUN,
  Permission.COMP_REPORT_EXPORT,
  Permission.COMP_OFFER_VIEW_WORKSPACE,
  Permission.COMP_OFFER_MANAGE_WORKSPACE,
  Permission.COMP_TRAINING_MANAGE,
] as const;

// recruitment (REC-000). Mirror of shore role-permission-arrays.hrms.ts
// (MHR_RECRUITMENT_*). Five all-human keys, three more for managers (raise and
// track your own requisition), fourteen admin keys on top.
const MHR_RECRUITMENT_ALL_HUMANS_PERMS = [
  Permission.JOB_OPENING_VIEW,
  Permission.REFERRAL_CREATE,
  Permission.REFERRAL_VIEW_OWN,
  Permission.INTERVIEW_VIEW_OWN,
  Permission.INTERVIEW_FEEDBACK_SUBMIT_OWN,
] as const;
const MHR_RECRUITMENT_MEMBER_PERMS = [...MHR_RECRUITMENT_ALL_HUMANS_PERMS] as const;
const MHR_RECRUITMENT_MANAGER_PERMS = [
  ...MHR_RECRUITMENT_ALL_HUMANS_PERMS,
  Permission.JOB_REQUISITION_CREATE,
  Permission.JOB_REQUISITION_VIEW_OWN,
  Permission.JOB_REQUISITION_CANCEL_OWN,
] as const;
const MHR_RECRUITMENT_ADMIN_PERMS = [
  ...MHR_RECRUITMENT_MANAGER_PERMS,
  Permission.STAFFING_PLAN_VIEW,
  Permission.STAFFING_PLAN_MANAGE,
  Permission.JOB_REQUISITION_VIEW_WORKSPACE,
  Permission.JOB_OPENING_MANAGE,
  Permission.APPLICANT_VIEW_WORKSPACE,
  Permission.APPLICANT_MANAGE_WORKSPACE,
  Permission.APPLICANT_HIRE_CONVERT_WORKSPACE,
  Permission.INTERVIEW_SCHEDULE_WORKSPACE,
  Permission.JOB_OFFER_VIEW_WORKSPACE,
  Permission.JOB_OFFER_MANAGE_WORKSPACE,
  Permission.REFERRAL_MANAGE_WORKSPACE,
  Permission.APPOINTMENT_LETTER_MANAGE_WORKSPACE,
  Permission.RECRUITMENT_REPORT_EXPORT,
] as const;

// M2 admin-tier perms, granted to Account Owner, Account Admin, Workspace Admin.
const M2_ADMIN_PERMS = [
  Permission.DEPARTMENT_CREATE,
  Permission.DEPARTMENT_EDIT,
  Permission.DEPARTMENT_DELETE,
  Permission.DEPARTMENT_RESTORE,
  Permission.DEPARTMENT_LIST,
  Permission.MEMBERSHIP_EDIT_MEMBER_IDENTITY,
  Permission.MEMBERSHIP_EDIT_HR_FIELDS,
  Permission.MEMBERSHIP_EDIT_SELF_PREFS,
  Permission.MEMBERSHIP_EDIT_MEMBER_PREFS,
  Permission.MEMBERSHIP_ASSIGN_MANAGER,
  Permission.WORKSPACE_IMPORT_USERS,
  Permission.WORKSPACE_BULK_EDIT_MEMBERS,
  Permission.WORKSPACE_BULK_ASSIGN_MANAGER,
  Permission.WORKSPACE_BULK_REMOVE_MEMBERS,
  Permission.WORKSPACE_BULK_CHANGE_ROLE,
  Permission.WORKSPACE_BULK_INVITE,
  Permission.MANAGER_VIEW_REPORTS,
  Permission.MANAGER_BULK_EDIT_REPORTS,
] as const;

const ACCOUNT_OWNER_PERMS: ReadonlySet<Permission> = new Set([
  Permission.ACCOUNT_CREATE_WORKSPACE,
  Permission.ACCOUNT_DELETE_WORKSPACE,
  Permission.ACCOUNT_RESTORE_WORKSPACE,
  Permission.ACCOUNT_GRANT_ADMIN,
  Permission.ACCOUNT_REVOKE_ADMIN,
  Permission.ACCOUNT_LIST_WORKSPACES,
  Permission.ACCOUNT_EDIT_NAME,
  Permission.ACCOUNT_MANAGE_AI_CONNECTIONS,
  Permission.WORKSPACE_VIEW,
  Permission.WORKSPACE_EDIT_SETTINGS,
  Permission.WORKSPACE_INVITE_MEMBER,
  Permission.WORKSPACE_REVOKE_INVITE,
  Permission.WORKSPACE_LIST_INVITATIONS,
  Permission.WORKSPACE_REMOVE_MEMBER,
  Permission.WORKSPACE_CHANGE_MEMBER_ROLE,
  Permission.WORKSPACE_LIST_MEMBERS,
  Permission.PROFILE_EDIT_SELF,
  Permission.PROFILE_VIEW_SELF,
  ...M2_ADMIN_PERMS,
  ...M3_ADMIN_PERMS,
  ...M4_ADMIN_PERMS,
  ...M5_ADMIN_PERMS,
  ...M6_ADMIN_PERMS,
  ...M7_ADMIN_PERMS,
  ...M8_ADMIN_PERMS,
  ...M8_ACCOUNT_ADMIN_PERMS,
  ...M9_ADMIN_PERMS,
  ...M10_ADMIN_PERMS,
  ...M11_ADMIN_PERMS,
  ...M12_ADMIN_PERMS,
  ...M13_ADMIN_PERMS,
  ...M14_ADMIN_PERMS,
  ...MGOALS_ADMIN_PERMS,
  ...M15_WRITING_ASSISTANT_ALL_HUMANS_PERMS,
  ...COPILOT_ALL_HUMANS_PERMS,
  ...M16_ADMIN_PERMS,
  ...M17_ADMIN_PERMS,
  ...MHR_HR_CORE_ADMIN_PERMS,
  ...MHR_HR_APPROVALS_ADMIN_PERMS,
  ...MHR_PAYROLL_ADMIN_PERMS,
  ...MHR_COMPENSATION_ADMIN_PERMS,
  ...MHR_RECRUITMENT_ADMIN_PERMS,
  // PAYD-FR-082, owner only and deliberately outside MHR_PAYROLL_ADMIN_PERMS,
  // matching shore. A lock every payroll admin can walk through is not a lock.
  Permission.PAYROLL_PERIOD_LOCK_OVERRIDE,
  ...MHR_EXPENSES_ADMIN_PERMS,
  // PAYD-FR-138, owner only for the same reason, matching shore. Erasure is
  // irreversible and it argues with retention: a payroll admin can run payroll
  // without being the person who decides a former employee's record goes.
  Permission.PAYROLL_ERASURE_REQUEST_MANAGE,
  Permission.AI_USAGE_VIEW,
  // Owner-only, matching shore. Redeeming an LTD code grants a plan the
  // same way a purchase does, so it gets the same "only the owner spends
  // the account's money" bar. BILLING_VIEW_ME is the account's payment
  // history, which is as sensitive as the spend that produced it. Both
  // are deliberately absent from ACCOUNT_ADMIN_PERMS.
  Permission.BILLING_REDEEM_CODE,
  Permission.BILLING_VIEW_ME,
]);
const ACCOUNT_ADMIN_PERMS: ReadonlySet<Permission> = new Set([
  Permission.ACCOUNT_CREATE_WORKSPACE,
  Permission.ACCOUNT_DELETE_WORKSPACE,
  Permission.ACCOUNT_RESTORE_WORKSPACE,
  Permission.ACCOUNT_LIST_WORKSPACES,
  Permission.ACCOUNT_EDIT_NAME,
  Permission.ACCOUNT_MANAGE_AI_CONNECTIONS,
  Permission.WORKSPACE_VIEW,
  Permission.WORKSPACE_EDIT_SETTINGS,
  Permission.WORKSPACE_INVITE_MEMBER,
  Permission.WORKSPACE_REVOKE_INVITE,
  Permission.WORKSPACE_LIST_INVITATIONS,
  Permission.WORKSPACE_REMOVE_MEMBER,
  Permission.WORKSPACE_CHANGE_MEMBER_ROLE,
  Permission.WORKSPACE_LIST_MEMBERS,
  Permission.PROFILE_EDIT_SELF,
  Permission.PROFILE_VIEW_SELF,
  ...M2_ADMIN_PERMS,
  ...M3_ADMIN_PERMS,
  ...M4_ADMIN_PERMS,
  ...M5_ADMIN_PERMS,
  ...M6_ADMIN_PERMS,
  ...M7_ADMIN_PERMS,
  ...M8_ADMIN_PERMS,
  ...M8_ACCOUNT_ADMIN_PERMS,
  ...M9_ADMIN_PERMS,
  ...M10_ADMIN_PERMS,
  ...M11_ADMIN_PERMS,
  ...M12_ADMIN_PERMS,
  ...M13_ADMIN_PERMS,
  ...M14_ADMIN_PERMS,
  ...MGOALS_ADMIN_PERMS,
  ...M15_WRITING_ASSISTANT_ALL_HUMANS_PERMS,
  ...COPILOT_ALL_HUMANS_PERMS,
  ...M16_ADMIN_PERMS,
  ...M17_ADMIN_PERMS,
  ...MHR_HR_CORE_ADMIN_PERMS,
  ...MHR_HR_APPROVALS_ADMIN_PERMS,
  ...MHR_PAYROLL_ADMIN_PERMS,
  ...MHR_COMPENSATION_ADMIN_PERMS,
  ...MHR_RECRUITMENT_ADMIN_PERMS,
  ...MHR_EXPENSES_ADMIN_PERMS,
  Permission.AI_USAGE_VIEW,
]);
const WORKSPACE_ADMIN_PERMS: ReadonlySet<Permission> = new Set([
  Permission.WORKSPACE_VIEW,
  Permission.WORKSPACE_EDIT_SETTINGS,
  Permission.WORKSPACE_INVITE_MEMBER,
  Permission.WORKSPACE_REVOKE_INVITE,
  Permission.WORKSPACE_LIST_INVITATIONS,
  Permission.WORKSPACE_REMOVE_MEMBER,
  Permission.WORKSPACE_CHANGE_MEMBER_ROLE,
  Permission.WORKSPACE_LIST_MEMBERS,
  Permission.WORKSPACE_LEAVE,
  Permission.PROFILE_EDIT_SELF,
  Permission.PROFILE_VIEW_SELF,
  ...M2_ADMIN_PERMS,
  ...M3_ADMIN_PERMS,
  ...M4_ADMIN_PERMS,
  ...M5_ADMIN_PERMS,
  ...M6_ADMIN_PERMS,
  ...M7_ADMIN_PERMS,
  ...M8_ADMIN_PERMS,
  ...M9_ADMIN_PERMS,
  ...M10_ADMIN_PERMS,
  ...M11_ADMIN_PERMS,
  ...M12_ADMIN_PERMS,
  ...M13_ADMIN_PERMS,
  ...M14_ADMIN_PERMS,
  ...MGOALS_ADMIN_PERMS,
  ...M15_WRITING_ASSISTANT_ALL_HUMANS_PERMS,
  ...COPILOT_ALL_HUMANS_PERMS,
  ...M16_ADMIN_PERMS,
  ...M17_ADMIN_PERMS,
  ...MHR_HR_CORE_ADMIN_PERMS,
  ...MHR_HR_APPROVALS_ADMIN_PERMS,
  ...MHR_PAYROLL_ADMIN_PERMS,
  ...MHR_COMPENSATION_ADMIN_PERMS,
  ...MHR_RECRUITMENT_ADMIN_PERMS,
  ...MHR_EXPENSES_ADMIN_PERMS,
]);
const WORKSPACE_MANAGER_PERMS: ReadonlySet<Permission> = new Set([
  Permission.WORKSPACE_VIEW,
  Permission.WORKSPACE_LIST_MEMBERS,
  Permission.WORKSPACE_LEAVE,
  Permission.PROFILE_EDIT_SELF,
  Permission.PROFILE_VIEW_SELF,
  Permission.MANAGER_VIEW_REPORTS,
  Permission.MANAGER_BULK_EDIT_REPORTS,
  Permission.MEMBERSHIP_EDIT_SELF_PREFS,
  Permission.DEPARTMENT_LIST,
  ...M3_MANAGER_PERMS,
  ...M4_MANAGER_PERMS,
  ...M5_MANAGER_PERMS,
  ...M6_MANAGER_PERMS,
  ...M7_NOTIFICATION_ALL_HUMANS_PERMS,
  ...M8_MANAGER_PERMS,
  ...M9_MANAGER_PERMS,
  ...M10_MANAGER_PERMS,
  ...M11_MANAGER_PERMS,
  ...M12_MANAGER_PERMS,
  ...M13_MANAGER_PERMS,
  ...M14_MANAGER_PERMS,
  ...MGOALS_MANAGER_PERMS,
  ...M15_WRITING_ASSISTANT_ALL_HUMANS_PERMS,
  ...COPILOT_ALL_HUMANS_PERMS,
  ...M16_MANAGER_PERMS,
  ...M17_MANAGER_PERMS,
  ...MHR_HR_CORE_MANAGER_PERMS,
  ...MHR_HR_APPROVALS_MANAGER_PERMS,
  ...MHR_PAYROLL_MANAGER_PERMS,
  ...MHR_COMPENSATION_MANAGER_PERMS,
  ...MHR_RECRUITMENT_MANAGER_PERMS,
  ...MHR_EXPENSES_MANAGER_PERMS,
]);
const WORKSPACE_MEMBER_PERMS: ReadonlySet<Permission> = new Set([
  Permission.WORKSPACE_VIEW,
  Permission.WORKSPACE_LIST_MEMBERS,
  Permission.WORKSPACE_LEAVE,
  Permission.PROFILE_EDIT_SELF,
  Permission.PROFILE_VIEW_SELF,
  Permission.MEMBERSHIP_EDIT_SELF_PREFS,
  Permission.DEPARTMENT_LIST,
  ...M3_MEMBER_PERMS,
  ...M4_MEMBER_PERMS,
  ...M5_MEMBER_PERMS,
  ...M6_MEMBER_PERMS,
  ...M7_MEMBER_PERMS,
  ...M8_MEMBER_PERMS,
  ...M9_MEMBER_PERMS,
  ...M10_MEMBER_PERMS,
  ...M11_MEMBER_PERMS,
  ...M12_MEMBER_PERMS,
  ...M13_MEMBER_PERMS,
  ...M14_MEMBER_PERMS,
  ...MGOALS_MEMBER_PERMS,
  ...M15_WRITING_ASSISTANT_ALL_HUMANS_PERMS,
  ...COPILOT_ALL_HUMANS_PERMS,
  ...M16_MEMBER_PERMS,
  ...M17_MEMBER_PERMS,
  ...MHR_HR_CORE_MEMBER_PERMS,
  ...MHR_HR_APPROVALS_MEMBER_PERMS,
  ...MHR_PAYROLL_MEMBER_PERMS,
  ...MHR_COMPENSATION_MEMBER_PERMS,
  ...MHR_RECRUITMENT_MEMBER_PERMS,
  ...MHR_EXPENSES_MEMBER_PERMS,
]);

export const roleMatrix = {
  accountRole: {
    [AccountRole.OWNER]: ACCOUNT_OWNER_PERMS,
    [AccountRole.ADMIN]: ACCOUNT_ADMIN_PERMS,
  },
  workspaceRole: {
    [WorkspaceRole.ADMIN]: WORKSPACE_ADMIN_PERMS,
    [WorkspaceRole.MANAGER]: WORKSPACE_MANAGER_PERMS,
    [WorkspaceRole.MEMBER]: WORKSPACE_MEMBER_PERMS,
  },
} as const;

export function canDo(
  permission: Permission,
  ctx: { accountRole?: AccountRole | null; workspaceRoles?: ReadonlyArray<WorkspaceRole> },
): boolean {
  if (permission === Permission.PUBLIC) return true;
  if (ctx.accountRole) {
    const set = roleMatrix.accountRole[ctx.accountRole];
    if (set?.has(permission)) return true;
  }
  for (const r of ctx.workspaceRoles ?? []) {
    if (roleMatrix.workspaceRole[r]?.has(permission)) return true;
  }
  return false;
}
